import type { BetLinkSet } from "@/lib/bet-links";
import type {
  OddsHistoryFairContext,
  OddsHistoryPeerBook,
  OddsHistoryQuoteCapacity,
  OddsHistorySelection,
  OddsHistorySelectionContext,
  OddsHistoryValueContext,
} from "@/components/odds/odds-history-selection";
import { getBookDisplayName } from "@/lib/utils/sports-books";

const OPPOSITE_SIDE: Record<string, string> = {
  over: "under",
  under: "over",
  yes: "no",
  no: "yes",
  home: "away",
  away: "home",
};

export function otherSideOf(side: string, line: number | null): { side: string; line: number | null } | null {
  const other = OPPOSITE_SIDE[side.toLowerCase()];
  return other ? { side: other, line: line != null && (other === "home" || other === "away") ? -line : line } : null;
}

export interface HistoryQuote {
  readonly book: string;
  readonly displayName: string;
  readonly selectionKey: string | null;
  readonly oppositeOf?: string;
  readonly oddsAmerican: number | null;
  readonly betLinks?: BetLinkSet;
  readonly capacity?: OddsHistoryQuoteCapacity;
  readonly isBestPrice?: true;
}

export type KeyedQuote = HistoryQuote & { readonly selectionKey: string };

export type ChartQuote =
  | KeyedQuote
  | (HistoryQuote & { readonly selectionKey: null; readonly oppositeOf: string });

export function isKeyedQuote(quote: HistoryQuote): quote is KeyedQuote {
  return quote.selectionKey != null;
}

type PricedQuote = HistoryQuote & { readonly oddsAmerican: number };

export interface HistoryLine {
  readonly line: number | null;
  readonly main: boolean;
  readonly quotes: readonly ChartQuote[];
  readonly priced: readonly PricedQuote[];
  readonly fair?: OddsHistoryFairContext;
  readonly value?: OddsHistoryValueContext;
}

export interface HistorySide {
  readonly side: string;
  readonly lines: readonly HistoryLine[];
}

export interface HistorySubject {
  readonly side: string;
  readonly line: number | null;
}

interface LineDraft {
  readonly line: number | null;
  readonly books: Map<string, HistoryQuote>;
  fair?: OddsHistoryFairContext;
  value?: OddsHistoryValueContext;
}

function sideOfKey(key: string): string {
  return key.slice(key.lastIndexOf("|") + 1);
}

function byPrice(a: HistoryQuote, b: HistoryQuote): number {
  return (b.oddsAmerican ?? -Infinity) - (a.oddsAmerican ?? -Infinity) || 0;
}

export function buildHistoryMarket(
  selection: OddsHistorySelection,
  peers: readonly OddsHistoryPeerBook[],
  pairs: ReadonlyMap<string, string>,
): readonly HistorySide[] {
  const sides = new Map<string, Map<string, LineDraft>>();
  const draft = (side: string, line: number | null): LineDraft => {
    const lines = sides.get(side) ?? new Map<string, LineDraft>();
    sides.set(side, lines);
    const found = lines.get(String(line)) ?? { line, books: new Map<string, HistoryQuote>() };
    lines.set(String(line), found);
    return found;
  };
  const add = (side: string, line: number | null, quote: HistoryQuote) => {
    const { books } = draft(side, line);
    const held = books.get(quote.book);
    if (held == null) {
      books.set(quote.book, quote);
      return;
    }
    const [first, second] = quote.isBestPrice && !held.isBestPrice ? [quote, held] : [held, quote];
    const selectionKey = held.selectionKey ?? quote.selectionKey;
    books.set(quote.book, {
      ...first,
      selectionKey,
      oppositeOf: selectionKey == null ? (held.oppositeOf ?? quote.oppositeOf) : undefined,
      oddsAmerican: first.oddsAmerican ?? second.oddsAmerican,
      betLinks:
        first.betLinks && second.betLinks
          ? {
              generic: first.betLinks.generic || second.betLinks.generic,
              mobile: first.betLinks.mobile || second.betLinks.mobile,
              desktop: first.betLinks.desktop || second.betLinks.desktop,
            }
          : (first.betLinks ?? second.betLinks),
      capacity: first.capacity ?? second.capacity,
      isBestPrice: first.isBestPrice ?? second.isBestPrice,
    });
  };
  const addPeer = (peer: OddsHistoryPeerBook, fallbackSide: string) => {
    const side = (peer.side ?? fallbackSide).toLowerCase();
    add(side, peer.line, {
      book: peer.book,
      displayName: peer.bookDisplayName,
      selectionKey: peer.selectionId ? `${peer.selectionId}|${side}` : null,
      oddsAmerican: Number.isFinite(peer.oddsAmerican) ? peer.oddsAmerican : null,
      betLinks: peer.betLinks,
      capacity: peer.capacity,
      isBestPrice: peer.isBestPrice,
    });
  };
  const addOwn = (
    side: string,
    line: number | null,
    selectionId: string | undefined,
    context: OddsHistorySelectionContext | undefined,
  ) => {
    const target = draft(side, line);
    target.fair = context?.fair;
    target.value = context?.value;
    if (selectionId) {
      const quote = context?.quote;
      add(side, line, {
        book: selection.book,
        displayName: getBookDisplayName(selection.book),
        selectionKey: `${selectionId}|${side}`,
        oddsAmerican: quote?.oddsAmerican != null && Number.isFinite(quote.oddsAmerican) ? quote.oddsAmerican : null,
        betLinks: quote?.betLinks,
        capacity: quote?.capacity,
        isBestPrice: quote?.isBestPrice,
      });
    }
  };

  const clickedSide = selection.side.toLowerCase();
  addOwn(clickedSide, selection.line, selection.selectionId, selection.context);
  for (const peer of peers) addPeer(peer, clickedSide);
  const opposite = selection.opposite;
  if (opposite) {
    const oppositeSide = opposite.side.toLowerCase();
    addOwn(oppositeSide, opposite.line, opposite.selectionId, opposite.context);
    for (const peer of opposite.peers ?? []) addPeer(peer, oppositeSide);
  }

  const derived = [...sides].flatMap(([side, lines]) =>
    [...lines.values()].flatMap(({ line, books }) =>
      [...books.values()].filter(isKeyedQuote).flatMap((quote) => {
        const other = pairs.get(quote.selectionKey);
        const otherSide = other == null ? otherSideOf(side, line)?.side : sideOfKey(other);
        if (otherSide == null) return [];
        const base = { book: quote.book, displayName: quote.displayName, oddsAmerican: null };
        return [{
          side: otherSide,
          line: otherSideOf(side, line)?.line ?? line,
          quote: other == null ? { ...base, selectionKey: null, oppositeOf: quote.selectionKey } : { ...base, selectionKey: other },
        }];
      }),
    ),
  );
  for (const entry of derived) {
    const quoted = sides.get(entry.side)?.get(String(entry.line))?.books.has(entry.quote.book) === true;
    if (quoted || (opposite == null && entry.quote.selectionKey != null)) {
      add(entry.side, entry.line, entry.quote);
    }
  }

  const mainOf = (side: string): number | null | undefined =>
    selection.mainLine == null
      ? undefined
      : side === clickedSide
        ? selection.mainLine
        : otherSideOf(clickedSide, selection.mainLine)?.line;
  return [clickedSide, ...[...sides.keys()].filter((side) => side !== clickedSide)].flatMap((side): HistorySide[] => {
    const main = mainOf(side);
    const lines = [...(sides.get(side)?.values() ?? [])]
      .flatMap((entry): HistoryLine[] => {
        const quotes = [...entry.books.values()];
        const chart = quotes.filter(
          (quote): quote is ChartQuote => quote.selectionKey != null || quote.oppositeOf != null,
        );
        return !chart.some(isKeyedQuote)
          ? []
          : [{
              line: entry.line,
              main: main !== undefined && main === entry.line,
              quotes: chart,
              priced: quotes.filter((quote): quote is PricedQuote => quote.oddsAmerican != null),
              fair: entry.fair,
              value: entry.value,
            }];
      })
      .sort((a, b) => (a.line ?? 0) - (b.line ?? 0));
    return lines.length === 0 ? [] : [{ side, lines }];
  });
}

export function nearestLine(side: HistorySide, target: number | null): HistoryLine {
  let best = side.lines[0];
  for (const entry of side.lines) {
    if (entry.line === target) return entry;
    if (target != null && entry.line != null && best.line != null && Math.abs(entry.line - target) < Math.abs(best.line - target)) {
      best = entry;
    }
  }
  return best;
}

export function resolveSubject(
  market: readonly HistorySide[],
  subject: HistorySubject,
): { side: HistorySide; line: HistoryLine } | null {
  const side = market.find((entry) => entry.side === subject.side) ?? market[0];
  return side == null ? null : { side, line: nearestLine(side, subject.line) };
}

export function switchSide(market: readonly HistorySide[], subject: HistorySubject, to: string): HistorySubject {
  const side = market.find((entry) => entry.side === to);
  if (side == null || to === subject.side) {
    return subject;
  }
  return { side: to, line: nearestLine(side, otherSideOf(subject.side, subject.line)?.line ?? subject.line).line };
}

export function bestCandidates(line: HistoryLine): readonly PricedQuote[] {
  return line.priced.filter((quote) => quote.isBestPrice === true);
}

export function anchorAt(line: HistoryLine, clickedBook: string): KeyedQuote {
  const keyed = line.quotes.filter(isKeyedQuote);
  const top = [...bestCandidates(line)].sort(byPrice)[0];
  return (
    keyed.find((quote) => quote.book === clickedBook) ??
    keyed.find((quote) => quote.book === top?.book) ??
    [...keyed].sort(byPrice)[0]
  );
}
